import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Uploader } from "@/pages/ops/missions/opsUi";
const SERVICES = [["rh", "RH"], ["technique", "Technique / Maintenance"], ["qualite", "Qualité"], ["direction", "Direction"], ["commercial", "Commercial"], ["achats", "Achats"], ["marketing", "Marketing"]];
import { AlertTriangle, Package, Wrench, Users, HelpCircle, Send, ArrowLeft, Clock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const ALERT_TYPES = [
  { key: "rupture", label: "Rupture produit", icon: Package, color: "amber" },
  { key: "equipement", label: "Problème équipement", icon: Wrench, color: "orange" },
  { key: "incident", label: "Incident client", icon: Users, color: "rose" },
  { key: "autre", label: "Autre", icon: HelpCircle, color: "slate" },
];

const URG_LEVELS = [
  { key: "normal", label: "Normal", color: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "important", label: "Important", color: "bg-amber-100 text-amber-700 border-amber-300" },
  { key: "urgent", label: "Urgent", color: "bg-rose-100 text-rose-700 border-rose-300" },
];

const URG_BADGE = {
  normal: "bg-slate-100 text-slate-700",
  important: "bg-amber-100 text-amber-700",
  urgent: "bg-rose-100 text-rose-700",
};

const STATUS_BADGE = {
  open: { label: "Ouverte", cls: "bg-amber-100 text-amber-700" },
  in_progress: { label: "En cours", cls: "bg-blue-100 text-blue-700" },
  closed: { label: "Fermée", cls: "bg-emerald-100 text-emerald-700" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AlertesTerrain({ embedded = false }) {
  const [type, setType] = useState("rupture");
  const [title, setTitle] = useState(""); const [reference, setReference] = useState(""); const [service, setService] = useState(""); const [photos, setPhotos] = useState([]);
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [sending, setSending] = useState(false);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alertes", { params: { limit: 50 } });
      setAlertes(data || []);
    } catch { setAlertes([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return toast.error("Description obligatoire");
    setSending(true);
    try {
      await api.post("/alertes", { type, description: description.trim(), urgency, title: title.trim() || null, reference: reference.trim() || null, service: service || null, photos });
      toast.success(service ? `Alerte transmise au service ${SERVICES.find(x => x[0] === service)?.[1] || service} ✓` : "Alerte envoyée à votre manager ✓");
      setDescription(""); setType("rupture"); setUrgency("normal"); setTitle(""); setReference(""); setService(""); setPhotos([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    }
    setSending(false);
  };

  return (
    <div className="space-y-5" data-testid="alertes-terrain-page">
      {/* Header */}
      {!embedded && <div className="flex items-center gap-3">
        <Link to="/kpi-corner" data-testid="back-btn" className="w-9 h-9 grid place-items-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">
          <ArrowLeft className="w-4 h-4"/>
        </Link>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Remonter un problème</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Alertes terrain 🚨</h1>
        </div>
      </div>}

      {/* Formulaire */}
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="alerte-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Type d'alerte</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {ALERT_TYPES.map(t => {
              const Icon = t.icon;
              const active = type === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setType(t.key)}
                        data-testid={`alerte-type-${t.key}`}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition ${active ? `border-${t.color}-500 bg-${t.color}-50 text-${t.color}-800 shadow` : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"}`}>
                  <Icon className="w-5 h-5"/>
                  <span className="text-xs font-semibold text-center leading-tight">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
                    data-testid="alerte-description"
                    rows={4} placeholder="Décrivez le problème rencontré…"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"/>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Niveau d'urgence</label>
          <div className="grid grid-cols-3 gap-2">
            {URG_LEVELS.map(l => {
              const active = urgency === l.key;
              return (
                <button key={l.key} type="button" onClick={() => setUrgency(l.key)}
                        data-testid={`alerte-urgency-${l.key}`}
                        className={`p-2.5 rounded-xl border-2 font-semibold text-sm transition ${active ? l.color : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Titre</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex : Vitrine froide en panne" className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm" data-testid="alerte-title" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Référence</label><input value={reference} onChange={e => setReference(e.target.value)} placeholder="N° produit, équipement, BL…" className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm" data-testid="alerte-reference" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Service destinataire</label><select value={service} onChange={e => setService(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm bg-white" data-testid="alerte-service"><option value="">Mon manager</option>{SERVICES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        </div>
        <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Photo / document</label><Uploader files={photos} onChange={setPhotos} label="Prendre une photo ou joindre un fichier" testid="alerte-photos" /></div>
        <button type="submit" disabled={sending || !description.trim()}
                data-testid="alerte-submit"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold shadow disabled:opacity-50">
          <Send className="w-4 h-4"/> {sending ? "Envoi…" : "Envoyer l'alerte"}
        </button>
      </form>

      {/* Historique */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="alertes-history">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-slate-800">Mes alertes envoyées</div>
          <div className="text-xs text-slate-500">{alertes.length} alerte{alertes.length > 1 ? "s" : ""}</div>
        </div>
        {loading ? (
          <div className="text-slate-400 text-sm text-center py-8">Chargement…</div>
        ) : alertes.length === 0 ? (
          <div className="text-center py-10">
            <AlertTriangle className="w-10 h-10 mx-auto text-slate-300 mb-2"/>
            <div className="text-sm text-slate-500">Aucune alerte pour le moment.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {alertes.map(a => {
              const st = STATUS_BADGE[a.status] || STATUS_BADGE.open;
              const typeMeta = ALERT_TYPES.find(t => t.key === a.type);
              const Icon = typeMeta?.icon || AlertTriangle;
              return (
                <div key={a.id} className="p-3 rounded-xl border border-slate-200 bg-slate-50/50" data-testid={`alerte-item-${a.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 grid place-items-center rounded-full bg-white text-slate-600 shrink-0"><Icon className="w-4 h-4"/></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">{typeMeta?.label || a.type}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${URG_BADGE[a.urgency] || URG_BADGE.normal}`}>{a.urgency}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="text-sm text-slate-700 mt-1">{a.description}</div>
                      <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                        <Clock className="w-3 h-3"/> {fmtDate(a.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
